import { takeLeading, put, call, select } from 'redux-saga/effects';
import { navigate, getLocation } from '../navigation';

import {
  actions as coreActions,
  getAccessToken,
  getRefreshToken,
  getTokenProvider,
  checkIfTokenCanBeUsed,
  isAccessTokenUsableWithDpopKey,
  TOKEN_PROVIDERS,
} from '@formue-app/core';

import { actions } from './actions';
import { initAnalytics } from '../../services/tracking/analytics';
import { actions as authActions } from '../auth';

const {
  entities: { operationalMessages, configuration },
  ui: { portfolio: portfolioActions },
} = coreActions;

function* initialize() {
  // Load configuration from API as soon as possible
  yield put(configuration.indexRequestBegin());
  // Operational messages are fetched here, not in the init handler below,
  // because public routes like /login never dispatch init. This is what puts
  // an outage banner on the login page.
  // Except on the way out to Microsoft (/auth/sso with no response in the hash
  // yet): that page leaves as soon as configuration loads, cancelling the request.
  const leavingForSso =
    window.location.pathname === '/auth/sso' && !window.location.hash;
  if (!leavingForSso) {
    yield put(operationalMessages.indexRequestBegin());
  }

  yield takeLeading(actions.init, function* () {
    const accessToken = yield call(getAccessToken);
    const refreshToken = yield call(getRefreshToken);
    // Which provider issued the stored session decides where it gets refreshed.
    const provider = yield call(getTokenProvider);

    // If we have a refresh token and not an access token, or an access token that's about to expire,
    // we want to exchange the refresh token for a new access token.
    if (refreshToken) {
      // An /oidc access token is also unusable when it is bound to a DPoP key
      // this browser no longer holds (cleared IndexedDB, another profile): the
      // API would refuse it, so refresh instead. If the refresh token is bound
      // to that same missing key the provider answers invalid_grant and the
      // existing failure path clears the session — self-healing, nothing to
      // add. A legacy session is never key-bound, so its path is unchanged.
      const tokenCanBeUsed =
        checkIfTokenCanBeUsed(accessToken) &&
        (provider !== TOKEN_PROVIDERS.OIDC ||
          (yield call(isAccessTokenUsableWithDpopKey, accessToken)));
      if (!tokenCanBeUsed) {
        return yield put(
          authActions.startAuthentication({
            exchange: 'refreshToken',
            credentials: {
              token: refreshToken,
            },
            provider,
          })
        );
      }
    }

    if (accessToken) {
      yield put(authActions.setAccessToken(accessToken));
      const isEmployee = yield select((state) => state.auth.isEmployee);

      // If we have an impersonation ID in session storage that means we are in impersonation
      // mode and rather than bootstrap the app in normal mode we kick of the impersonate action
      // as we would do if the advisors selected the user manually in the list.
      if (isEmployee) {
        yield put(authActions.loadCurrentUser());
        const impersonationId =
          window.sessionStorage.getItem('impersonationId');

        if (impersonationId) {
          // We need to reset impersonation first to make sure we have cleared all data
          yield put(authActions.resetImpersonation());
          yield put(authActions.impersonateUser(impersonationId));
        } else {
          // If this is an employee, but we have not selected a client yet, make sure they are
          // redirected to the client select screen.
          yield call(navigate, '/advisor/clients');
          // After the navigate, so the landing view is the picker (untracked),
          // not the page the employee is redirected away from.
          yield call(initAnalytics);
        }
      } else {
        yield put(actions.load());
        yield call(initAnalytics);
      }
    } else {
      const location = yield call(getLocation);
      const pathname = location?.pathname;
      if (pathname !== '/login') {
        yield call(navigate, '/login');
      }
    }
  });
}

function* load() {
  yield takeLeading(actions.load, function* ({ payload: ssid }) {
    // Trigger another fetch after full app load to avoid missing messages
    // due to early init timing issues.
    yield put(operationalMessages.indexRequestBegin());
    yield put(authActions.loadCurrentUser());

    yield put(portfolioActions.init(ssid));
  });
}

export const sagas = [initialize, load];
